// World edits: pixel props placed in tile space by World Studio (docs/WORLD_EDITOR.md). Visual only: no collision,
// no gameplay. The shipped document is common/data/world-edits.json; the game draws it in the 2x art only.
import {
  PixelAsset,
  isFlatFamily,
  readAssetList,
} from "./pixel-assets";
import { GameMap, T, tileAt } from "./map";

/** Assets one world document may carry (the packs the studios exchange have their own cap). */
export const MAX_WORLD_ASSETS = 32;
/** Props one draft may hold. The game budget (config.json worldProps) is much smaller: see worldEditsProblems. */
export const MAX_DRAFT_PROPS = 1000;

export interface WorldProp {
  id: string;
  assetId: string;
  /** Tile coordinates of the foot anchor, fractions allowed. */
  x: number;
  y: number;
  /** Mirrored left to right. Upright props can only mirror. */
  flip?: true;
  /** Flat props (manhole, drain, tactile paving) may also turn 180 degrees. */
  rotation?: 180;
}
export interface WorldEdits {
  version: 1;
  name: string;
  assets: PixelAsset[];
  props: WorldProp[];
}
export const emptyEdits = (): WorldEdits => ({
  version: 1,
  name: "World edits",
  assets: [],
  props: [],
});

export function readEdits(value: unknown, map: GameMap): WorldEdits {
  const v = value as WorldEdits;
  if (
    !v ||
    v.version !== 1 ||
    typeof v.name !== "string" ||
    v.name.length > 100 ||
    !Array.isArray(v.props) ||
    v.props.length > MAX_DRAFT_PROPS
  )
    throw Error(
      `Expected a version 1 world edit with at most ${MAX_DRAFT_PROPS} props.`,
    );
  const assets = readAssetList(v.assets ?? [], MAX_WORLD_ASSETS),
    byId = new Map(assets.map((a) => [a.id, a])),
    ids = new Set<string>();
  const props = v.props.map((p): WorldProp => {
    const asset = p && typeof p.assetId === "string" ? byId.get(p.assetId) : undefined;
    if (
      !p ||
      typeof p.id !== "string" ||
      !p.id ||
      p.id.length > 100 ||
      ids.has(p.id) ||
      !asset ||
      !Number.isFinite(p.x) ||
      !Number.isFinite(p.y) ||
      p.x < 0 ||
      p.y < 0 ||
      p.x >= map.w ||
      p.y >= map.h
    )
      throw Error("Invalid prop: ID, asset reference or position.");
    // Drafts from the first studio build carried `kind` (built-ins are gone), `rotation` 0-270 and a `track`.
    const kind = (p as { kind?: unknown }).kind;
    if (kind !== undefined && kind !== "custom")
      throw Error("Built-in prop kinds were removed; use a generated asset.");
    const flip = (p as { flip?: unknown }).flip,
      rotation = (p as { rotation?: unknown }).rotation;
    if (flip !== undefined && typeof flip !== "boolean")
      throw Error("Invalid prop: flip must be true or false.");
    if (rotation !== undefined && rotation !== 0 && rotation !== 180)
      throw Error("Invalid prop: only flat props turn, by 180 degrees.");
    if (rotation === 180 && !isFlatFamily(asset.recipe.family))
      throw Error("Invalid prop: upright props can only be mirrored.");
    ids.add(p.id);
    // Only the documented fields are kept, never arbitrary imported properties.
    return {
      id: p.id,
      assetId: p.assetId,
      x: p.x,
      y: p.y,
      ...(flip ? { flip: true as const } : {}),
      ...(rotation === 180 ? { rotation: 180 as const } : {}),
    };
  });
  return { version: 1, name: v.name, assets, props };
}

/** Why a prop cannot stand here, or undefined. Sidewalks, plazas and alleys; flat props (manholes, drains) may also
 *  lie on roads, standing props never; never a solid tile. */
export function blockedReason(
  map: GameMap,
  p: { x: number; y: number },
  flat = false,
): string | undefined {
  if (p.x < 0 || p.y < 0 || p.x >= map.w || p.y >= map.h)
    return "Outside the city.";
  const t = tileAt(map, Math.floor(p.x), Math.floor(p.y));
  if (t === T.SIDEWALK || t === T.PLAZA || t === T.ALLEY) return undefined;
  if (t === T.ROAD)
    return flat
      ? undefined
      : "Standing props never stand on roads: choose a sidewalk, plaza or alley.";
  return "Blocked ground: choose a sidewalk, plaza or alley.";
}

export interface PropBudget {
  maxTotal: number;
  maxPerScene: number;
}
export interface SceneRect {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}
/** Everything the game requires of a world document: ground, a scene to draw it in, and the prop budget
 *  (config.json worldProps, invariant 7). The game skips the whole document when this list is not empty. */
export function worldEditsProblems(
  edits: WorldEdits,
  map: GameMap,
  scenes: SceneRect[],
  budget: PropBudget,
): string[] {
  const out: string[] = [];
  if (edits.props.length > budget.maxTotal)
    out.push(`${edits.props.length} props exceed the budget of ${budget.maxTotal}.`);
  const perScene = new Map<string, number>();
  for (const p of edits.props) {
    const asset = edits.assets.find((a) => a.id === p.assetId);
    const why = blockedReason(map, p, !!asset && isFlatFamily(asset.recipe.family));
    if (why) out.push(`${p.id}: ${why}`);
    const tx = Math.floor(p.x),
      ty = Math.floor(p.y),
      s = scenes.find(
        (r) => tx >= r.x && ty >= r.y && tx < r.x + r.w && ty < r.y + r.h,
      );
    if (!s) out.push(`${p.id}: outside every HD scene, so it would never draw.`);
    else perScene.set(s.id, (perScene.get(s.id) ?? 0) + 1);
  }
  for (const [id, n] of perScene)
    if (n > budget.maxPerScene)
      out.push(`Scene ${id} has ${n} props; the budget is ${budget.maxPerScene}.`);
  return out;
}
